import React, { useMemo } from 'react';
import { Box, Chip, Paper, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import moment from 'moment';
import { THEME_COLORS } from '../../shared/constants/theme';
import { getDashboardDarkSurfaceTokens } from '../../utils/dashboardSurfaceSx';
import { getBookingCurrencySymbol } from '../../utils/bookingPaymentSummary';

function resolveRoomChips({ allocatedRooms, roomTypeName, formRooms }) {
  if (Array.isArray(allocatedRooms) && allocatedRooms.length > 0) {
    return allocatedRooms.map((r, idx) => ({
      key: `alloc-${r.room_id || idx}`,
      label: r.room_number
        ? `${r.room_number} · ${r.room_type_name || 'Room'}`
        : (r.room_type_name || 'Room'),
    }));
  }

  const chips = [];
  if (Array.isArray(formRooms) && formRooms.length > 0) {
    formRooms.forEach((room, idx) => {
      const count = Math.max(1, Number(room.rooms) || 1);
      const typeName = room.roomType || room.room_type_name || 'Room';
      for (let i = 0; i < count; i += 1) {
        chips.push({
          key: `form-${idx}-${i}`,
          label: typeName,
        });
      }
    });
  }

  if (!chips.length && roomTypeName) {
    String(roomTypeName)
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((name, idx) => {
        chips.push({ key: `type-${idx}`, label: name });
      });
  }

  return chips;
}

/**
 * Compact confirmation card for reservation / walk-in success dialogs.
 */
export default function BookingConfirmedSummaryCard({
  isDark,
  accent = '#4ce1b6',
  bookingCode,
  groupBookingCode = null,
  codeLabel = 'Booking code',
  checkInDate,
  checkOutDate,
  guestName,
  guestPhone,
  roomsCount,
  adults,
  children,
  amount,
  currency = 'INR',
  allocatedRooms = [],
  roomTypeName = '',
  formRooms = [],
  badgeLabel = null,
}) {
  const roomChips = useMemo(
    () => resolveRoomChips({ allocatedRooms, roomTypeName, formRooms }),
    [allocatedRooms, roomTypeName, formRooms],
  );
  const currencySymbol = getBookingCurrencySymbol(currency);

  const formatDate = (value) => {
    if (!value) return '—';
    if (moment.isMoment(value)) return value.format('DD MMM YYYY');
    const m = moment(value);
    return m.isValid() ? m.format('DD MMM YYYY') : '—';
  };

  const metaItems = [
    { label: 'Check-in', value: formatDate(checkInDate) },
    { label: 'Check-out', value: formatDate(checkOutDate) },
    { label: 'Guest', value: guestName || '—' },
    { label: 'Phone', value: guestPhone || '—' },
    { label: 'Rooms', value: roomsCount ?? '—' },
    { label: 'Adults', value: adults ?? '—' },
    { label: 'Children', value: children ?? 0 },
  ];

  const surface = getDashboardDarkSurfaceTokens(isDark);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.25,
        mb: 1,
        borderRadius: 2,
        background: isDark
          ? `linear-gradient(160deg, ${alpha(accent, 0.1)} 0%, ${THEME_COLORS.BACKGROUND_DARK} 48%)`
          : `linear-gradient(160deg, ${alpha(accent, 0.08)} 0%, #fff 55%)`,
        backgroundColor: isDark ? surface.surface : '#ffffff',
        border: `1px solid ${isDark ? alpha(accent, 0.28) : alpha(accent, 0.22)}`,
        boxShadow: isDark
          ? `0 8px 22px ${alpha('#000', 0.35)}`
          : `0 6px 18px ${alpha(accent, 0.12)}`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: '0.65rem',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: isDark ? alpha('#fff', 0.55) : alpha('#0f172a', 0.5),
              mb: 0.2,
            }}
          >
            {codeLabel}
          </Typography>
          <Typography
            sx={{
              color: accent,
              fontWeight: 800,
              fontSize: '0.95rem',
              letterSpacing: '0.02em',
              lineHeight: 1.2,
            }}
          >
            {bookingCode || 'N/A'}
          </Typography>
          {groupBookingCode ? (
            <Typography
              sx={{
                mt: 0.35,
                fontSize: '0.68rem',
                fontWeight: 600,
                color: isDark ? alpha('#fff', 0.65) : alpha('#0f172a', 0.55),
              }}
            >
              Group · {groupBookingCode}
            </Typography>
          ) : null}
        </Box>
        {badgeLabel ? (
          <Chip
            size="small"
            label={badgeLabel}
            sx={{
              height: 22,
              fontSize: '0.65rem',
              fontWeight: 700,
              bgcolor: alpha(accent, isDark ? 0.2 : 0.14),
              color: accent,
              border: `1px solid ${alpha(accent, 0.35)}`,
              '& .MuiChip-label': { px: 0.85 },
            }}
          />
        ) : null}
      </Box>

      {roomChips.length > 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.6,
            mb: 1.1,
          }}
        >
          {roomChips.map((chip) => (
            <Chip
              key={chip.key}
              size="small"
              icon={<MeetingRoomOutlinedIcon sx={{ fontSize: '0.95rem !important' }} />}
              label={chip.label}
              sx={{
                height: 26,
                maxWidth: '100%',
                fontWeight: 700,
                fontSize: '0.72rem',
                color: isDark ? '#f1f5f9' : '#0f172a',
                bgcolor: isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.04),
                border: `1px solid ${isDark ? alpha('#fff', 0.14) : alpha('#0f172a', 0.1)}`,
                '& .MuiChip-icon': {
                  color: accent,
                  ml: '6px',
                },
                '& .MuiChip-label': {
                  px: 0.85,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              }}
            />
          ))}
        </Box>
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: 0.75,
        }}
      >
        {metaItems.map((item) => (
          <Box key={item.label} sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '0.62rem',
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: isDark ? alpha('#fff', 0.45) : alpha('#0f172a', 0.45),
                mb: 0.15,
              }}
            >
              {item.label}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.78rem',
                fontWeight: 600,
                color: isDark ? '#e2e8f0' : '#0f172a',
                lineHeight: 1.25,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {item.value}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mt: 1.1,
          pt: 1,
          borderTop: `1px solid ${isDark ? alpha('#fff', 0.1) : alpha('#0f172a', 0.08)}`,
        }}
      >
        <Typography
          sx={{
            fontSize: '0.7rem',
            fontWeight: 700,
            color: isDark ? alpha('#fff', 0.5) : alpha('#0f172a', 0.5),
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          Amount
        </Typography>
        <Typography sx={{ color: accent, fontWeight: 800, fontSize: '1.05rem', lineHeight: 1 }}>
          {currencySymbol}{Number(amount || 0).toFixed(2)}
        </Typography>
      </Box>
    </Paper>
  );
}
